import type React from "react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRoles } from "@/hooks/useRoles";
import type { IconProps } from "@/interfaces/IconProps";

/**
 * Internal interface extending IconProps with additional tooltip-specific properties.
 * This interface is only used within the IconWithTooltip component.
 */
interface IconWithTooltipProps extends IconProps {
	tooltip?: string;
	onClick?: () => void;
	isDefaultValue?: boolean;
	showTooltip?: boolean;
}

/**
 * IconWithTooltip Component
 *
 * Renders an icon with role-based state and a portal-rendered tooltip.
 * Supports two icon types: "edit" and "reset" with conditional enabling/disabling
 * based on user role and data state.
 *
 * Features:
 * - Role-based icon rendering (admin vs non-admin)
 * - Dynamic tooltips with mouse tracking
 * - Portal rendering for proper z-index layering in AG Grid
 * - Keyboard accessibility support
 *
 * @param iconName - Type of icon to render ("edit" or "reset")
 * @param size - Icon dimensions in pixels
 * @param className
 * @param style
 * @param alt
 * @param tooltip
 * @param isDefaultValue - Whether the value is already set to default (affects reset icon state)
 * @param onClick - Click handler (only fires when icon is enabled)
 * @param showTooltip - When false, tooltip will not be shown (useful in tests)
 */
export const IconWithTooltip: React.FC<IconWithTooltipProps> = ({
	iconName = "Edit",
	size = 20,
	className,
	style,
	alt = "Icon",
	tooltip,
	onClick,
	isDefaultValue = false,
	showTooltip = true,
}) => {
	const { isAdmin } = useRoles();
	const [visible, setVisible] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const wrapperRef = useRef<HTMLSpanElement>(null);
	const tooltipRef = useRef<HTMLSpanElement | null>(null);

	/**
	 * Determines icon state based on icon type and user role.
	 * Returns the appropriate icon source, tooltip text, and clickability.
	 */
	const getIconState = () => {
		const name = (iconName || "").toLowerCase();

		// Edit icon: enabled for admins only
		if (name === "edit") {
			return {
				src: isAdmin ? "/images/svg/pencil-blue.svg" : "/images/svg/pencil-grey.svg",
				tooltip: isAdmin ? "Edit" : "Edit only available for Admins",
				isClickable: isAdmin,
			};
		}

		// Reset icon: disabled if not admin OR value is already default
		if (name === "reset") {
			const isDisabled = !isAdmin || isDefaultValue;
			const src = isDisabled
				? "/images/svg/reset-in-circle-grey.svg"
				: "/images/svg/reset-in-circle-blue.svg";

			// Determine tooltip message based on state
			let tooltipText = "Set to default";
			if (!isAdmin) {
				tooltipText = "Reset to default only available for Admins";
			} else if (isDefaultValue) {
				tooltipText = "Already set to default";
			}

			return {
				src,
				tooltip: tooltipText,
				isClickable: isAdmin && !isDefaultValue,
			};
		}

		// Fallback for unknown icon types
		return {
			src: "/images/svg/pencil-grey.svg",
			tooltip: tooltip || "",
			isClickable: false,
		};
	};

	const iconState = getIconState();

	/**
	 * Updates tooltip position to follow mouse cursor.
	 * Positions tooltip 30px above cursor with horizontal centering.
	 */
	const handleMouseMove = (e: React.MouseEvent) => {
		if (!wrapperRef.current) return;

		// Desired position: centered on cursor X, 30px above cursor Y
		let desiredX = e.clientX;
		let desiredY = e.clientY - 30;

		// Measure tooltip size if available
		const tooltipEl = tooltipRef.current;
		const tooltipWidth = tooltipEl ? tooltipEl.offsetWidth : 200;
		const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 24;

		const margin = 8; // keep tooltip away from viewport edge
		const half = Math.round(tooltipWidth / 2);

		// Compute min/max allowed center X (considering translateX(-50%))
		const minCenterX = margin + half;
		const maxCenterX = Math.max(window.innerWidth - margin - half, minCenterX);

		// Clamp desiredX to allowed range
		desiredX = Math.min(maxCenterX, Math.max(minCenterX, desiredX));

		// Ensure tooltip Y does not go above top of viewport
		const minY = margin + tooltipHeight; // leave some space
		if (desiredY < minY) {
			desiredY = minY;
		}

		setPosition({ x: desiredX, y: desiredY });
	};

	// Utility function to convert number to pixel string
	const toPx = (v: number | string) => (typeof v === "number" ? `${v}px` : v);

	// Icon wrapper styling
	const wrapperStyle: React.CSSProperties = {
		position: "relative",
		display: "inline-flex",
		alignItems: "center",
		cursor: iconState.isClickable ? "pointer" : "default",
		width: toPx(size),
		height: toPx(size),
		...style,
	};

	// Tooltip styling with fixed positioning for portal rendering
	const tooltipStyle: React.CSSProperties = {
		position: "fixed",
		top: position.y,
		left: position.x,
		transform: "translateX(-50%)",
		background: "rgba(0, 0, 0, 0.85)",
		color: "#fff",
		padding: "6px 10px",
		borderRadius: 4,
		fontSize: 13,
		whiteSpace: "nowrap",
		pointerEvents: "none",
		zIndex: 9999, // Ensures tooltip appears above AG Grid cells
		boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
		opacity: visible ? 1 : 0,
		transition: "opacity 150ms ease-in-out",
		maxWidth: "320px", // Prevent tooltip from expanding too much
		overflow: "hidden", // Ensure content doesn't overflow
		textOverflow: "ellipsis", // Add ellipsis for long text
		boxSizing: "border-box",
	};

	return (
		<>
			{/* Icon wrapper with accessibility attributes */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: pre-existing tooltip wrapper conditionally becomes a button via role/tabIndex when clickable */}
			{/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-label intentionally always describes the icon regardless of interactive state */}
			<span
				ref={wrapperRef}
				role={iconState.isClickable ? "button" : undefined}
				tabIndex={iconState.isClickable ? 0 : undefined}
				aria-label={alt}
				aria-disabled={!iconState.isClickable}
				style={wrapperStyle}
				className={className}
				onMouseEnter={() => setVisible(true)}
				onMouseLeave={() => setVisible(false)}
				onMouseMove={handleMouseMove}
				onFocus={() => setVisible(true)}
				onBlur={() => setVisible(false)}
				onClick={iconState.isClickable && onClick ? onClick : undefined}
			>
				<img
					src={iconState.src}
					alt={alt}
					style={{
						width: toPx(size), // Set width based on size prop
						height: toPx(size),
						display: "block",
						opacity: iconState.isClickable ? 1 : 0.5, // Dimmed when disabled
					}}
				/>
			</span>
			{/* Portal-rendered tooltip attached to document.body to bypass AG Grid overflow will be hidden at dialog appearance*/}
			{visible &&
				showTooltip &&
				createPortal(
					<span ref={(el) => (tooltipRef.current = el)} style={tooltipStyle} aria-hidden={!visible}>
						{/* When icon is disabled, show caution font icon before the tooltip text */}
						{!iconState.isClickable ? (
							<span
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 8,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									maxWidth: "100%",
								}}
							>
								<i
									className="icon-warning"
									aria-hidden={true}
									style={{
										fontSize: 14,
										lineHeight: "14px",
										display: "inline-block",
										color: "#F59E0B",
										flex: "0 0 auto",
									}}
								/>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{iconState.tooltip}
								</span>
							</span>
						) : (
							<span
								style={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									display: "inline-block",
									maxWidth: "100%",
								}}
							>
								{iconState.tooltip}
							</span>
						)}
					</span>,
					document.body,
				)}
		</>
	);
};

/**
 * Icon Component
 *
 * Simple wrapper around IconWithTooltip for backward compatibility.
 * Can be used when tooltip functionality is not needed.
 */
export const Icon: React.FC<IconProps> = (props) => {
	return <IconWithTooltip {...props} />;
};

/**
 * Lightweight Google Material Symbols SVG icons used to replace @mui/icons-material.
 * Each icon accepts standard SVG props (className, style, etc.) and uses
 * fill="currentColor" so color is controlled via Tailwind text-* classes.
 * Source paths taken from the provided /public/images/svg google icon assets.
 */
type SvgIconProps = React.SVGProps<SVGSVGElement>;

const createSvgIcon = (path: string, displayName: string) => {
	const SvgIcon: React.FC<SvgIconProps> = ({ className, ...props }) => (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			height="1em"
			viewBox="0 -960 960 960"
			width="1em"
			fill="currentColor"
			className={className}
			aria-hidden="true"
			{...props}
		>
			<path d={path} />
		</svg>
	);
	SvgIcon.displayName = displayName;
	return SvgIcon;
};

export const ChevronLeftIcon = createSvgIcon(
	"M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z",
	"ChevronLeftIcon",
);

export const ChevronRightIcon = createSvgIcon(
	"M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z",
	"ChevronRightIcon",
);

export const FirstPageIcon = createSvgIcon(
	"M240-240v-480h80v480h-80Zm440 0L440-480l240-240 56 56-184 184 184 184-56 56Z",
	"FirstPageIcon",
);

export const LastPageIcon = createSvgIcon(
	"m280-240-56-56 184-184-184-184 56-56 240 240-240 240Zm360 0v-480h80v480h-80Z",
	"LastPageIcon",
);

export const SubdirectoryArrowRightIcon = createSvgIcon(
	"m560-120-57-57 144-143H200v-480h80v400h367L503-544l56-57 241 241-240 240Z",
	"SubdirectoryArrowRightIcon",
);

export default Icon;
